import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { Loader2 } from 'lucide-react';

const WorkshopsEnrolled = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [enrolledWorkshops, setEnrolledWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leaveDialog, setLeaveDialog] = useState({ show: false, workshopId: null, workshopTitle: '' });

  useEffect(() => {
    const fetchEnrolledWorkshops = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch enrolled workshops for the current user
        const { data: enrollments, error: enrollmentError } = await supabase
          .from('workshop_enrollments')
          .select('id, enrolled_at, workshop_id')
          .eq('user_id', user.id)
          .order('enrolled_at', { ascending: false });

        if (enrollmentError) throw enrollmentError;

        if (!enrollments || enrollments.length === 0) {
          setEnrolledWorkshops([]);
          return;
        }

        // Fetch workshop details for enrolled workshops
        const workshopIds = enrollments.map(e => e.workshop_id);
        const { data: workshops, error: workshopsError } = await supabase
          .from('workshops')
          .select('*')
          .in('id', workshopIds);

        if (workshopsError) throw workshopsError;

        // Combine enrollment data with workshop details
        const transformedWorkshops = enrollments.map(enrollment => {
          const workshop = workshops.find(w => w.id === enrollment.workshop_id);
          return {
            enrollmentId: enrollment.id,
            enrolledAt: enrollment.enrolled_at,
            ...workshop
          };
        }).filter(w => w.id); // Filter out any missing workshops

        setEnrolledWorkshops(transformedWorkshops);
      } catch (err) {
        console.error('Error fetching enrolled workshops:', err);
        setError('Failed to load enrolled workshops');
      } finally {
        setLoading(false);
      }
    };

    fetchEnrolledWorkshops();
  }, [user]);

  const handleViewAnnouncements = (workshopId) => {
    navigate(`/workshop-announcements/${workshopId}`);
  };

  const handleLeaveClick = (workshopId, workshopTitle) => {
    setLeaveDialog({ show: true, workshopId, workshopTitle });
  };

  const handleConfirmLeave = async () => {
    const { workshopId } = leaveDialog;
    
    try {
      const workshop = enrolledWorkshops.find(w => w.id === workshopId);
      if (!workshop) return;

      const { error } = await supabase
        .from('workshop_enrollments')
        .delete()
        .eq('id', workshop.enrollmentId);

      if (error) throw error;

      setEnrolledWorkshops(prev => prev.filter(w => w.id !== workshopId));
      setLeaveDialog({ show: false, workshopId: null, workshopTitle: '' });
    } catch (err) {
      console.error('Error leaving workshop:', err);
      alert('Failed to leave workshop. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBA';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <>
        <Helmet>
          <title>Enrolled Workshops - UniSteps</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="container mx-auto px-4 py-8 max-w-7xl pt-28">
            <div className="text-center py-12">
              <Icon name="Lock" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-2">Authentication Required</h2>
              <p className="text-muted-foreground mb-6">
                Please log in to view your enrolled workshops.
              </p>
              <Button onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Enrolled Workshops - UniSteps</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-4 py-8 max-w-7xl pt-28">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">My Enrolled Workshops</h1>
            <p className="text-muted-foreground">
              View and manage workshops you're enrolled in
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6">
              <p className="text-destructive font-medium">{error}</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && enrolledWorkshops.length === 0 && (
            <div className="text-center py-12">
              <Icon name="BookOpen" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-2">No Enrolled Workshops</h2>
              <p className="text-muted-foreground mb-6">
                You haven't enrolled in any workshops yet. Discover available workshops to get started.
              </p>
              <Button onClick={() => navigate('/workshop-catalog')}>
                <Icon name="Search" className="w-4 h-4 mr-2" />
                Discover Workshops
              </Button>
            </div>
          )}

          {/* Workshops Grid */}
          {!loading && !error && enrolledWorkshops.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {enrolledWorkshops.map((workshop) => (
                <div
                  key={workshop.id}
                  className="glass-panel glass-stroke border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all"
                >
                  {/* Workshop Image */}
                  {workshop.images && workshop.images.length > 0 && (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={workshop.images[0]}
                        alt={workshop.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 right-3">
                        <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                          Enrolled
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Workshop Details */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 line-clamp-2">
                      {workshop.title}
                    </h3>
                    
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {workshop.description}
                    </p>

                    {/* Workshop Meta */}
                    <div className="space-y-2 mb-4">
                      {workshop.instructor && (
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="User" size={16} className="text-muted-foreground" />
                          <span className="text-foreground">{workshop.instructor}</span>
                        </div>
                      )}
                      
                      {workshop.start_date && (
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="Calendar" size={16} className="text-muted-foreground" />
                          <span className="text-foreground">{formatDate(workshop.start_date)}</span>
                        </div>
                      )}

                      {workshop.format && (
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name={workshop.format === 'online' ? 'Monitor' : 'MapPin'} size={16} className="text-muted-foreground" />
                          <span className="text-foreground capitalize">{workshop.format}</span>
                        </div>
                      )}

                      {workshop.difficulty && (
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="BarChart" size={16} className="text-muted-foreground" />
                          <span className="text-foreground">{workshop.difficulty}</span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleViewAnnouncements(workshop.id)}
                        className="flex-1"
                        variant="outline"
                      >
                        <Icon name="Bell" className="w-4 h-4 mr-2" />
                        Announcements
                      </Button>
                      <Button
                        onClick={() => handleLeaveClick(workshop.id, workshop.title)}
                        className="flex-1"
                        variant="destructive"
                      >
                        <Icon name="LogOut" className="w-4 h-4 mr-2" />
                        Leave
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

        <ConfirmDialog
          isOpen={leaveDialog.show}
          title="Leave Workshop"
          message={`Are you sure you want to leave "${leaveDialog.workshopTitle}"?`}
          onConfirm={handleConfirmLeave}
          onCancel={() => setLeaveDialog({ show: false, workshopId: null, workshopTitle: '' })}
          confirmText="Leave"
          cancelText="Cancel"
          isDangerous={true}
        />
      </div>
    </>
  );
};

export default WorkshopsEnrolled;
